import { faArrowRotateLeft } from '@fortawesome/pro-light-svg-icons';
import {
  IconLinkButton,
  PageTitle,
} from '@foxtrotplatform/developer-platform-common-ui-component-lib';
import {
  useOrganization,
  useTeam,
} from '@foxtrotplatform/platform-micro-site-core';
import {
  Badge,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHeader,
  TableRow,
} from '@windmill/react-ui';
import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';

import { inviteUser } from '../../common/api/team';
import { addToast } from '../../common/reducers/toasterSlice';
import { RoleTypeComponent } from './HelperComponents';

interface InviteeMemberProps {
  invitee: Invitee;
  resendInvite: (invitee: Invitee) => void;
  deleteInvite: (invitee: Invitee) => void;
  removeFromTable: (id: string) => void;
}

const InviteeMember: React.FunctionComponent<InviteeMemberProps> = ({
  invitee,
  resendInvite,
}) => {
  const getBadgeTypeByStatus = (status: string) => {
    switch (status) {
      case 'DECLINED':
        return 'danger';
      case 'SENT':
        return 'warning';
      case 'ACCEPTED':
        return 'success';
      default:
        return 'neutral';
    }
  };

  return (
    <TableRow>
      <TableCell className="w-3/5">
        <span>{invitee.email}</span>
      </TableCell>
      <TableCell className="w-1/4">
        <RoleTypeComponent role={invitee.role} />
      </TableCell>
      <TableCell className="w-1/4">
        <Badge
          className="mb-px"
          type={`${getBadgeTypeByStatus(invitee.status)}`}
        >
          {invitee.status}
        </Badge>
      </TableCell>
      <TableCell>
        <IconLinkButton
          icon={faArrowRotateLeft}
          linkText="Resend Invite"
          onClick={() => resendInvite(invitee)}
        />
      </TableCell>
    </TableRow>
  );
};

interface InviteeTableProps {
  inviteeList: Invitee[];
  removeFromTable: (id: string) => void;
  deleteInvite: (invitee: Invitee) => void;
}

export const InviteeTable: FunctionComponent<InviteeTableProps> = ({
  inviteeList,
  removeFromTable,
  deleteInvite,
}): JSX.Element => {
  const [pageTable, setPageTable] = useState(1);
  const [dataTable, setDataTable] = useState<Invitee[]>([]);
  const dispatch = useDispatch();
  const resultsPerPage = 5;
  const { currentOrg } = useOrganization();
  const { currentTeam } = useTeam();

  useEffect(() => {
    setDataTable(
      inviteeList.slice(
        (pageTable - 1) * resultsPerPage,
        pageTable * resultsPerPage
      )
    );
  }, [pageTable, inviteeList]);

  const onPageChange = (pageNumber: number) => {
    setPageTable(pageNumber);
  };

  const resendInvite = async (invitee: Invitee) => {
    try {
      await inviteUser(
        currentOrg?.id as string,
        currentTeam?.id as string,
        invitee.email,
        invitee.role
      );

      const newToastState = {
        message: `Invite successfully resent to: ${invitee.email} (${invitee.role} role)`,
        isError: false,
      } as ToasterState;
      dispatch(addToast(newToastState));
    } catch {
      const newToastState = {
        message: `Invite did not successfully resend to: ${invitee.email} (${invitee.role} role)`,
        isError: true,
      } as ToasterState;
      dispatch(addToast(newToastState));
    }
  };

  const mapInvitees = useCallback(() => {
    if (!dataTable) {
      return;
    }

    return (
      <TableBody>
        {dataTable.map((invitee, i) => (
          <InviteeMember
            deleteInvite={deleteInvite}
            invitee={invitee}
            key={`${invitee.email}_${i}`}
            removeFromTable={removeFromTable}
            resendInvite={resendInvite}
          />
        ))}
      </TableBody>
    );
  }, [dataTable]);

  return (
    <div>
      <PageTitle className="mb-2">Invited Members</PageTitle>
      <TableContainer>
        <Table>
          <TableHeader>
            <TableRow>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHeader>
          {mapInvitees()}
        </Table>
        <TableFooter>
          <Pagination
            label="Table navigation"
            onChange={onPageChange}
            resultsPerPage={resultsPerPage}
            totalResults={inviteeList.length}
          />
        </TableFooter>
      </TableContainer>
    </div>
  );
};
